import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BFF, environment } from '@core/envs';
import { CountryRulesV2, ICountryMask, IDealerByLocation, IFeatureToggle, IInitialData, IRecall, IResponseBFF, IUserFromBFF, IVehicleDetailsBFF } from '@core/interface';
import { map, switchMap, firstValueFrom } from 'rxjs';
import { FeatureToggleLibService } from '../../feature-toggle/feature-toggle.service';
import { UserService } from '../../user/user.service';
import { RulesService } from '../../rules-service/rules.service';
import { MasksService } from '../../masks/masks.service';
import { DealerService } from '../../dealer/dealer.service';
import { VehicleService } from '../../vehicle/vehicle.service';
import { TranslateService } from '@ngx-translate/core';
import { RecallService } from '../../recall/recall.service';
import { PreferredDealerService } from '../../dealer/dealer-preferred.service';
import { MyGarageService } from '../../vehicle/garage/my-garage.service';

@Injectable({
  providedIn: 'root'
})
export class InitSystemService {
  readonly #http = inject(HttpClient);
  readonly #translate = inject(TranslateService);

  // mapeando informações iniciais do sistema em outros serviços
  readonly #featureToggleService = inject(FeatureToggleLibService);
  readonly #userService = inject(UserService);
  readonly #mygarageService = inject(MyGarageService);
  readonly #rulesService = inject(RulesService);
  readonly #masksService = inject(MasksService);
  readonly #dealerService = inject(DealerService);
  readonly #preferredDealerService = inject(PreferredDealerService);
  readonly #recallService = inject(RecallService);
  readonly #vehicleService = inject(VehicleService);

  async initialData(payload: {
    vin: string;
    country: string;
    region: string;
    dealer: number;
    applicationCode: string
  }) {
    const api = environment.getRoute(BFF.scheduling, false, false);
    const url = `${api}/initial-data`;

    await firstValueFrom(
      this.#http.post<IResponseBFF<IInitialData>>(url, payload).pipe(
        switchMap(async response => {
          const { result } = response;
          const {
            customer,
            featureToggles,
            rules,
            masks,
            dealer,
            vehicleDetails,
            vehicle
          } = result;

          // Mapeando flags de feature toggle
          if (featureToggles?.length) {
            this.#featureToggleService.mapAndSaveFlags(featureToggles as IFeatureToggle[]);
          }

          // Mapeando regras do país
          if (rules) {
            this.#rulesService.setRules(rules as CountryRulesV2);
          }

          // Mapeando máscaras
          if (masks) {
            this.#masksService.setMasks(masks as ICountryMask);
          }

          // Mapeando usuário (aguarda finalizar)
          if (customer) {
            // Define o idioma do usuário
            this.#defineLanguage((customer.languages?.preferredLanguage ?? '') as string);
            await this.#userService.setDataUser(customer as IUserFromBFF);
          }

          // Mapeando dealer por id via parametro URL
          if (dealer) {
            await this.#dealerService.setAndBuildDealer(
              dealer as IDealerByLocation,
              this.#userService.userCoordinates
            );
          }

          this.#getPreferredDealerByVin({
            vin: payload.vin,
            country: payload.country,
            modelGroup: String(vehicleDetails?.modelGroup || '')
          });

          // Mapeando detalhes do veículos do usuário
          if (vehicleDetails || vehicle) {
            const vehicleDetailsCombined = {
              ...vehicleDetails,
              ...vehicle
            }

            this.#vehicleService.setVehicleDetails(vehicleDetailsCombined as IVehicleDetailsBFF);
          };

          // Mapeando recalls abertos
          if (response?.result?.recalls) {
            this.#recallService.setRecalls(response.result.recalls as IRecall[]);
          }

          return response.result;
        }),
        map(result => result)
      )
    );
  }

  #defineLanguage(lang: string): void {
    this.#translate.setDefaultLang(lang);
    this.#translate.use(lang);
  }

  #getPreferredDealerByVin(search: {
    vin: string,
    country: string,
    modelGroup: string
  }): void {
  const vehicles = this.#mygarageService.userVehicles();
  const preferredDealerVin = vehicles?.find(v => v.vin === search.vin)?.preferredDealer || '';
  if (preferredDealerVin) {
    this.#preferredDealerService.fetchDealerDetails(
      preferredDealerVin,
      search.country,
      search.modelGroup
    )
  }
}
}
